import PropTypes from 'prop-types'
import {HTTPNotFound} from 'pwa-kit-react-sdk/ssr/universal/errors'
import React, {useEffect} from 'react'
import {useCommerceAPI} from '../../commerce-api/contexts'
import useAsync from '../../commerce-api/hooks/useAsync'
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../components/product-tile'

/**
 * Display product tile for a given product ID.
 *
 * @param {string} pid - Product ID
 *
 * @param {string} type - Product tile type, optional
 *
 * @example
 *   <pwa-widget-product pid="101001" type="look">
 *     $include('Widget-Product', 'pid', '#(product.product_id)', 'type', 'plptile')$
 *   </pwa-widget-product>
 */
const WidgetProduct = ({pid, type}) => {
    const api = useCommerceAPI()
    const {loading, error, data, execute} = useAsync(async (id) => {
        const product = await api.shopperProducts.getProduct({parameters: {id, allImages: true}})
        // Copied from ProductDetail.getProps
        // The `commerce-isomorphic-sdk` package does not throw errors, so
        // we have to check the returned object type to inconsistencies.
        if (typeof product?.type === 'string') {
            throw new HTTPNotFound(product.detail)
        }
        return product
    })
    const productTileVariant = type === 'look' ? 'Quickshop' : 'Default'

    useEffect(() => {
        if (pid) {
            execute(pid)
        }
    }, [execute, pid])

    if (!pid) {
        console.error(`No product ID provided`)
        return null
    }

    if (loading) {
        return <ProductTileSkeleton />
    }

    if (error) {
        console.error(`Something bad happened while loading ${pid}\n${error.toString()}`)
        return null
    }

    // prevent initial flash of undefined product
    if (!data) {
        return null
    }

    return <ProductTile product={data} variant={productTileVariant} />
}

WidgetProduct.defaultProps = {
    type: 'default'
}

WidgetProduct.propTypes = {
    pid: PropTypes.string,
    type: PropTypes.string
}

export default WidgetProduct
